import React, { useState } from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { IClusterItem } from 'components/widgets/Gantt/components/ganttChart.types';
import { GanttSettingsModal } from 'components/widgets/Gantt/components/ganttSettingsModal';
import { useSharedResourcesAvailabilityLogic } from 'components/widgets/SharedResourcesAvailability/SharedResourcesAvailability.logic';
import classes from 'components/widgets/SharedResourcesAvailability/SharedResourcesAvailability.module.scss';
import { useSharedResourcesAvailabilityProvider } from 'components/widgets/SharedResourcesAvailability/SharedResourcesAvailability.provider';
import GanttChartDisplay from 'components/widgets/SharedResourcesAvailability/ganttChartDisplay/ganttChartDisplay';

const { componentKey, id, endpoint } = widgetsConfigurations.sharedResourcesAvailability;

export const SharedResourcesAvailability = ({
    latestVersionId,
    widgetId,
    externalComponents,
    projectId,
    contractId,
    localFilters,
    editNarrative,
    setSubTitleForExport,
    globalFilterData,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.sharedResourcesAvailability.title,
    setIsPayloadReady,
}: IComponentProps) => {
    const [showSettings, setShowSettings] = useState(false);

    const { ui, api } = useSharedResourcesAvailabilityProvider({
        widgetId,
        latestVersionId,
        localFilters,
        widgetViewMode,
    });

    const { logic } = useSharedResourcesAvailabilityLogic({
        ui,
        api,
        globalFilterData,
        setIsPayloadReady,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getSharedResourcesAvailability.payload,
        widgetEndpoint: endpoint,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <WidgetButton icon={'zoom_in_map'} onClick={logic.zoomToFit} tooltipContent={`Zoom To Fit`} />,
                    <WidgetButton
                        icon={'fullscreen'}
                        onClick={logic.handleFullscreen}
                        tooltipContent={`Full Screen`}
                    />,
                    !widgetId && (
                        <WidgetButton
                            icon={'tune'}
                            onClick={() => setShowSettings(true)}
                            tooltipContent={`Gantt Settings`}
                        />
                    ),
                    <AddWidget
                        key={'addWidget'}
                        title={title}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldUseNewIcon={true}
                        shouldHide={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getSharedResourcesAvailability.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getSharedResourcesAvailability.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <div className={classes.localFilters} key={'localFilters'}>
                            <div className={classes.clusterSelectFilterWrapper}>
                                <label>
                                    Select Cluster
                                    <span className={classes.countOfTotal}>
                                        {logic.selectedClusterCountOfTotalTitle}
                                    </span>
                                </label>
                                <Select<IClusterItem>
                                    value={ui.state.cluster}
                                    options={logic.clustersList}
                                    onChange={ui.setters.setCluster}
                                    className={classes.clusterSelect}
                                />
                            </div>

                            <div className={classes.criticalPathFilterWrapper}>
                                <Switch
                                    onChange={(e) => ui.setters.setIsCriticalPath(e.target.checked)}
                                    checked={ui.state.isCriticalPath}
                                    label={'Critical Path Only'}
                                    labelPlacement={'start'}
                                />
                            </div>
                        </div>
                    ),
                ]}
            >
                <div ref={logic.ref} className={classes.chartContainer}>
                    {logic.isAnyGlobalFilterApplied ? (
                        <NoData>Not applicable with the applied filter</NoData>
                    ) : (
                        <GanttChartDisplay
                            isFullScreen={logic.isFullScreen}
                            ganttData={api.getSharedResourcesAvailability.instance.data}
                            isLoading={api.getSharedResourcesAvailability.instance.isFetching}
                            getInstanceAgain={logic.getInstance}
                            isCriticalPath={ui.state.isCriticalPath}
                            selectedCluster={logic.selectedCluster}
                            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? classes.chart : ''}
                        />
                    )}
                </div>
            </WidgetWithTitle>
            <GanttSettingsModal showModal={showSettings} onHideModal={() => setShowSettings(false)} />
        </WidgetExistInBackend>
    );
};
